//eslint-disable-next-line
//@ts-nocheck
import {
  Button,
  Card,
  InputNumber,
  Modal,
  Progress,
  Select,
  Table,
  Tooltip,
} from "antd";
import axios from "axios";
import _ from "lodash";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useProject } from "../../App/state";
import { useFloorplan } from "../../components/FloorplanEditor/hooks/useFloorplan";
import { useNotification } from "../../contexts/Notifications";
import {
  useGetMappedFloorplansQuery,
  useGetProjectSiteConfigQuery,
  useGetSequenceDetailsQuery,
  useGetSpaceAncestorNamesByIdQuery,
  useUpdateProjectOpenSpaceSiteMappingMutation,
} from "../../generated/types";
import { ClickablePlot } from "./ClickablePlot";
import styles from "./OpenSpaceContainer.module.css";
import { PanoViewerModal } from "./PanoViewerModal";
import { SheetMappingModal } from "./SheetMappingModal";
import { REGION_OPTIONS } from "./regions";

interface Organization {
  name: string;
  id: string;
}

interface Site {
  name: string;
  id: string;
}

interface SiteSheet {
  name: string;
  id: string;
  imageId?: string;
  siteId?: string;
}

interface Capture {
  id: string;
  startedAt: string;
}

interface Pano {
  id: string;
  sheetPose: {
    position: any;
  };
  captureId: string;
  sharpnessScore: number;
}

const getDisperseRegionFromHostname = (hostname: string): string | null => {
  const match = hostname.match(/^core(?:-[^.]+)?\.([^.]+)\.disperse\.io$/);
  return match ? match[1] : null;
};

export const OpenSpaceContainer: React.FC = () => {
  const baseURL = axios.create({
    baseURL: `${process.env["OPENSPACE_MAPPING_SERVICE"]}`,
    timeout: 1500000,
    withCredentials: true,
  });

  const floorplansEditor = useFloorplan();
  const notify = useNotification();
  const [updateProjectOpenSpaceSiteMapping] =
    useUpdateProjectOpenSpaceSiteMappingMutation();

  const [isSyncModalVisible, setIsSyncModalVisible] = useState(false);
  const [pendingSites, setPendingSites] = useState<string[]>([]);

  const [selectedOrganization, setSelectedOrganization] = useState<
    [string, string] | null
  >(null);
  const [selectedProject, setSelectedProject] = useState<string | undefined>(
    undefined,
  );
  const [selectedCapture, setSelectedCapture] = useState<
    [string, string][] | null
  >(null);
  const [loading, setLoading] = useState(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [siteSheets, setSiteSheets] = useState<SiteSheet[]>([]);
  const [selectedSiteSheet, setSelectedSiteSheet] = useState<
    string | undefined
  >(undefined);
  const [captures, setCaptures] = useState<Capture[]>([]);
  const [panos, setPanos] = useState<any[]>([]);
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");
  const [uploadPath, setUploadPath] = useState("");
  const [uploadLogs, setUploadLogs] = useState<string[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [xTranslate, setXTranslate] = useState(0);
  const [yTranslate, setYTranslate] = useState(0);
  const [adjustableScale, setAdjustableScale] = useState(1);
  const [avgDistanceOffset, setAvgDistanceOffset] = useState(0);
  const [selectedRegion, setSelectedRegion] = useState<string>("ksa");
  const [selectedScanLevel, setSelectedScanLevel] = useState<string>("");
  const [noSequencePoints, setNoSequencePoints] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [panoModalVisible, setPanoModalVisible] = useState(false);
  const [panoImageUrl, setPanoImageUrl] = useState<string | null>(null);
  const [panoLoading, setPanoLoading] = useState(false);
  const [panoError, setPanoError] = useState<string | null>(null);
  const [mappingModalVisible, setMappingModalVisible] = useState(false);
  const [selectedPanoPoint, setSelectedPanoPoint] = useState<string | null>(
    null,
  );
  const [captureDataLoaded, setCaptureDataLoaded] = useState(false);
  const [captureLoading, setCaptureLoading] = useState(false);

  const floorplanIdToSpaceId = floorplansEditor.floorplanIdToSpaceId;

  const project = useProject();
  const { customer, project: projectName, scope } = project;

  const { data: projectSiteConfigData, refetch: refetchProjectSiteConfig } =
    useGetProjectSiteConfigQuery({
      variables: { customer, project: projectName },
    });
  const savedConfig = projectSiteConfigData?.projects?.[0]?.config;
  const syncedSites = savedConfig?.sites ?? [];

  const handleOrganizationChange = useCallback(
    async (value: [string, string]) => {
      setSelectedOrganization(value);
      setSelectedProject(undefined);
      setSelectedCapture(null);
      setCaptureDataLoaded(false);
      try {
        setGeneralError(null);
        const response = await baseURL.get(`/get-sites/${value[0]}`, {
          params: {
            region: selectedRegion,
            customer: project.customer,
            project: project.project,
          },
        });
        setSites(response.data.content);
      } catch (error) {
        console.error("Error fetching sites:", error);
        if (axios.isAxiosError(error)) {
          setGeneralError(
            `Error fetching sites: ${
              error.response?.data?.detail || error.message
            }`,
          );
        } else {
          setGeneralError("An unexpected error occurred while fetching sites.");
        }
      }
    },
    [baseURL, project.customer, project.project, selectedRegion],
  );

  useEffect(() => {
    if (!savedConfig) {
      return;
    }

    if (savedConfig.region && !selectedOrganization) {
      setSelectedRegion(savedConfig.region.toLowerCase());
    }

    if (
      savedConfig.organization &&
      organizations.length > 0 &&
      !selectedOrganization
    ) {
      const org = organizations.find((o) => o.id === savedConfig.organization);
      if (org) {
        handleOrganizationChange([org.id, org.name]);
      }
    }
  }, [
    savedConfig,
    organizations,
    selectedOrganization,
    handleOrganizationChange,
  ]);

  const { data: mappedFloorplansData, refetch: refetchMappedFloorplans } =
    useGetMappedFloorplansQuery({
      variables: { customer, project: projectName, scope },
    });

  const { data: floorplanSpacesData } = useGetSpaceAncestorNamesByIdQuery({
    variables: {
      tenant: project,
      spaceIds: Object.values(floorplanIdToSpaceId),
    },
    skip: !Object.values(floorplanIdToSpaceId).length,
  });

  const { data: sequenceDetailsData } = useGetSequenceDetailsQuery({
    variables: {
      tenant: project,
      floorPlanId: floorplansEditor.floorplan?.id ?? "",
      scanLevel: selectedScanLevel,
    },
    skip: !floorplansEditor.floorplan?.id,
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      setNoSequencePoints(!data.getSequenceDetails?.length);
    },
  });

  useEffect(() => {
    setNoSequencePoints(false);
  }, [floorplansEditor.floorplan?.id, selectedScanLevel]);

  const selectFloorplanOptions = useMemo(() => {
    if (floorplanSpacesData?.spacesByFilter && selectedSiteSheet) {
      const floorplanSpacesMap = _.keyBy(
        floorplanSpacesData?.spacesByFilter,
        "id",
      );

      const mappedFloorplans = mappedFloorplansData?.mappedFloorplans || [];
      const mappedFloorplanIds = new Set(
        mappedFloorplans
          .filter((fp) =>
            fp.osSheetMappings?.some(
              (mapping) => mapping.osSheetId === selectedSiteSheet,
            ),
          )
          .map((fp) => fp.id),
      );

      return _.sortBy(
        Object.entries(floorplanIdToSpaceId)
          .filter(([key]) => mappedFloorplanIds.has(key))
          .map(([key, value]) => ({
            id: key,
            label: `${floorplanSpacesMap[value].name} - ${floorplanSpacesMap[
              value
            ].ancestors
              .map((s) => s.name)
              .join("-")}`,
          })),
        "label",
      );
    } else {
      return [];
    }
  }, [
    floorplanSpacesData,
    floorplanIdToSpaceId,
    mappedFloorplansData,
    selectedSiteSheet,
  ]);

  useEffect(() => {
    const fetchOrganizations = async () => {
      if (!selectedRegion) {
        return;
      }
      try {
        setGeneralError(null);
        const response = await baseURL.get(`/get-organization`, {
          params: {
            region: selectedRegion,
            customer: project.customer,
            project: project.project,
          },
        });
        setOrganizations(response.data.content);
      } catch (error) {
        console.error("Error fetching organizations:", error);
        if (axios.isAxiosError(error)) {
          setGeneralError(
            `Error fetching organizations: ${
              error.response?.data?.detail || error.message
            }`,
          );
        } else {
          setGeneralError(
            "An unexpected error occurred while fetching organizations.",
          );
        }
      }
    };

    fetchOrganizations();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegion]);

  const handleProjectChange = async (value: string) => {
    setSelectedProject(value);
    setSelectedSiteSheet(undefined);
    setCaptureDataLoaded(false);
    try {
      setGeneralError(null);
      const response = await baseURL.get(`/get-site-sheets/${value}`, {
        params: {
          region: selectedRegion,
          customer: project.customer,
          project: project.project,
        },
      });
      setSiteSheets(response.data.content);
    } catch (error) {
      console.error("Error fetching site sheets:", error);
      if (axios.isAxiosError(error)) {
        setGeneralError(
          `Error fetching site sheets: ${
            error.response?.data?.detail || error.message
          }`,
        );
      } else {
        setGeneralError(
          "An unexpected error occurred while fetching site sheets.",
        );
      }
    }
  };

  const handleCaptureChange = async (values: [string, string][]) => {
    setSelectedCapture(values);
    setCaptureDataLoaded(false);
    setCaptureLoading(true);
    try {
      setGeneralError(null);
      const requests = values.map((id) =>
        baseURL.get(`/panos-by-capture/${selectedProject}/${id[0]}`, {
          params: {
            region: selectedRegion,
            customer: project.customer,
            project: project.project,
          },
        }),
      );
      const responses = await Promise.all(requests);

      // Check if all responses are successful (status 200)
      const allSuccessful = responses.every(
        (response) => response.status === 200,
      );

      if (allSuccessful) {
        const allPanos = responses.flatMap((response) => response.data.content);
        const transformedPanos = allPanos.map((pano: Pano) => ({
          id: pano.id,
          location: pano.sheetPose.position,
          captureId: pano.captureId,
          sharpnessScore: pano.sharpnessScore,
        }));
        setPanos(transformedPanos);
        setCaptureDataLoaded(true);
      } else {
        setCaptureDataLoaded(false);
        setGeneralError("Some capture requests failed");
      }
    } catch (error) {
      console.error("Error fetching panos:", error);
      setCaptureDataLoaded(false);
      if (axios.isAxiosError(error)) {
        setGeneralError(
          `Error fetching panoramas: ${
            error.response?.data?.detail || error.message
          }`,
        );
      } else {
        setGeneralError(
          "An unexpected error occurred while fetching panoramas.",
        );
      }
    } finally {
      setCaptureLoading(false);
    }
  };

  const handleSiteSheetChange = async (value: string) => {
    setSelectedSiteSheet(value);
    setCaptureDataLoaded(false);
    try {
      setGeneralError(null);
      const response = await baseURL.get(
        `/captures-by-sheet/${selectedProject}/${value}`,
        {
          params: {
            region: selectedRegion,
            customer: project.customer,
            project: project.project,
          },
        },
      );
      setCaptures(response.data.content);
    } catch (error) {
      console.error("Error fetching captures:", error);
      if (axios.isAxiosError(error)) {
        setGeneralError(
          `Error fetching captures: ${
            error.response?.data?.detail || error.message
          }`,
        );
      } else {
        setGeneralError(
          "An unexpected error occurred while fetching captures.",
        );
      }
    }
  };

  const [mappingResponse, setMappingResponse] = useState<any | null>(null);

  const fetchPanoramaImage = async (
    siteId: string,
    captureId: string,
    panoId: string,
  ) => {
    try {
      const response = await baseURL.get("/get-image", {
        params: {
          site_id: siteId,
          capture_id: captureId,
          pano_id: panoId,
          region: selectedRegion,
          customer: project.customer,
          project: project.project,
        },
        responseType: "blob",
      });

      // Convert blob to data URL for panorama viewer
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        setPanoImageUrl(dataUrl);
        setPanoLoading(false); // Stop loading when image is ready
      };
      reader.onerror = () => {
        console.error("FileReader error");
        setPanoError("Failed to process image data");
      };
      reader.readAsDataURL(response.data);
    } catch (error) {
      console.error("Error fetching panorama image:", error);
      if (axios.isAxiosError(error)) {
        setPanoError(
          `Error fetching panorama: ${
            error.response?.data?.detail || error.message
          }`,
        );
      } else {
        setPanoError("An unexpected error occurred while fetching panorama.");
      }
    } finally {
      setPanoLoading(false);
    }
  };

  const handlePanoModalClose = () => {
    setPanoModalVisible(false);
    if (panoImageUrl) {
      URL.revokeObjectURL(panoImageUrl);
      setPanoImageUrl(null);
    }
    setPanoError(null);
    setSelectedPanoPoint(null);
  };

  const handlePointClick = (point: {
    x: number;
    y: number;
    id: string;
    captureId: string;
    sequenceNumber?: number;
  }) => {
    if (selectedProject) {
      // Open modal immediately with loading state
      setPanoModalVisible(true);
      setPanoLoading(true);
      setPanoError(null);
      setSelectedPanoPoint(
        point.sequenceNumber ? point.sequenceNumber.toString() : point.id,
      );

      // Then fetch the image
      fetchPanoramaImage(selectedProject, point.captureId, point.id);
    }
  };

  const downloadFloorplan = async () => {
    if (!selectedProject || !selectedSiteSheet) {
      setGeneralError("Please select both a project and sheet to download.");
      return;
    }

    try {
      setGeneralError(null);
      setDownloadLoading(true);

      const response = await baseURL.get(
        `/floorplan/${selectedProject}/${selectedSiteSheet}`,
        {
          params: {
            region: selectedRegion,
            customer: project.customer,
            project: project.project,
          },
          responseType: "blob", // Important for downloading files
        },
      );

      // Get the selected sheet name for the filename
      const selectedSheet = siteSheets.find(
        (sheet) => sheet.id === selectedSiteSheet,
      );
      const filename = selectedSheet
        ? `${selectedSheet.name}.png`
        : `floorplan_${selectedSiteSheet}.png`;

      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: "image/png" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading floorplan:", error);
      if (axios.isAxiosError(error)) {
        setGeneralError(
          `Error downloading floorplan: ${
            error.response?.data?.detail || error.message
          }`,
        );
      } else {
        setGeneralError(
          "An unexpected error occurred while downloading floorplan.",
        );
      }
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleMapping = async () => {
    if (!sequenceDetailsData || !floorplansEditor.floorplan) {
      console.error("Insufficient data to send mapping.");
      setGeneralError(
        "Insufficient data for mapping. Please ensure all required fields are filled.",
      );
      return;
    }

    const { height, width, originX, originY, mmScaleFactor, url } =
      floorplansEditor.floorplan;
    const sequencePoints = sequenceDetailsData?.getSequenceDetails;
    const payload = {
      sequencePoints,
      height,
      width,
      originX,
      originY,
      mmScaleFactor,
      url,
      xTranslate,
      yTranslate,
      adjustableScale,
      openspace_locations: panos,
      avgDistanceOffset,
    };

    try {
      setGeneralError(null);
      setLoading(true);
      const response = await baseURL.post("/sequence-points", payload, {
        params: {
          region: selectedRegion,
          customer: project.customer,
          project: project.project,
        },
      });
      setMappingResponse(response.data);
    } catch (error) {
      console.error("Error sending mapping data:", error);
      if (axios.isAxiosError(error)) {
        setGeneralError(
          `Mapping error: ${error.response?.data?.detail || error.message}`,
        );
      } else {
        setGeneralError("An unexpected error occurred during mapping.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    setUploadLoading(true);
    setUploadProgress(0);
    setUploadStatus("");
    setUploadPath("");
    setUploadLogs([]);
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const sharpnessScores = panos.map((pano) => pano.sharpnessScore);

      const payload = {
        capture_ids: mappingResponse?.filtered_points?.capture_ids,
        pano_ids: mappingResponse?.filtered_points?.ids,
        project_name: selectedOrganization ? selectedOrganization[1] : "",
        date_name: selectedCapture && selectedCapture[0][1],
        site_id: selectedProject,
        floor_name: selectFloorplanOptions.find(
          (option) => option.id === floorplansEditor.floorplan?.id,
        )?.label,
        region: selectedRegion,
        sequencenumbers: mappingResponse?.filtered_points?.sequencenumbers,
        sharpness_scores: sharpnessScores,
      };

      const url = new URL(
        `${process.env["OPENSPACE_MAPPING_SERVICE"]}/upload-equi-images`,
      );
      url.searchParams.append("region", selectedRegion);
      url.searchParams.append("customer", project.customer);
      url.searchParams.append("project", project.project);
      const disperseRegion = getDisperseRegionFromHostname(
        window.location.hostname,
      );
      if (disperseRegion) {
        url.searchParams.append("disperse-region", disperseRegion);
      }

      const response = await fetch(url.toString(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
        credentials: "include",
      });

      if (!response.body) {
        throw new Error("ReadableStream not supported in this browser.");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let { value, done } = await reader.read();
      let buffer = "";

      while (!done) {
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || ""; // Keep the last partial line

        for (const line of lines) {
          setUploadLogs((prevLogs) => [...prevLogs, line]);
          if (line.startsWith("Progress:")) {
            const match = line.match(/Progress:\s+(\d+)%\s+\((\d+)\/(\d+)\)/);
            if (match) {
              const percent = parseInt(match[1], 10);
              const current = parseInt(match[2], 10);
              const total = parseInt(match[3], 10);
              setUploadProgress(percent);
              setUploadStatus(`Progress: ${percent}% (${current}/${total})`);
            }
          } else if (line.startsWith("S3 Path:")) {
            setUploadPath(line);
          }
        }

        ({ value, done } = await reader.read());
      }

      // Handle any remaining buffer
      if (buffer.length > 0 && buffer.startsWith("Progress:")) {
        const match = buffer.match(/Progress:\s+(\d+)%\s+\((\d+)\/(\d+)\)/);
        if (match) {
          const percent = parseInt(match[1], 10);
          setUploadProgress(percent);
          setUploadStatus(`Progress: ${percent}%`);
        }
      }

      // Check the last log for status
      const lastLog = uploadLogs[uploadLogs.length - 1];
      if (lastLog && lastLog.startsWith("Please upload")) {
        setUploadStatus("Re-upload required.");
      } else {
        setUploadStatus("Upload completed successfully.");
      }
    } catch (error: any) {
      if (error.name === "AbortError") {
        setUploadStatus("Upload aborted by the user.");
      } else {
        setUploadStatus(`Error uploading images: ${error.message}`);
        console.error("Error uploading images:", error);
      }
    } finally {
      setUploadLoading(false);
      abortControllerRef.current = null;
    }
  };

  const showUploadModal = () => {
    setIsUploadModalVisible(true);
    setUploadStatus("");
    setUploadPath("");
  };

  const handleUploadCancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setIsUploadModalVisible(false);
    setUploadStatus("");
    setUploadPath("");
    setUploadLogs([]); // Clear logs on cancel
  };

  const handleOpenSyncModal = () => {
    setPendingSites([...syncedSites]);
    setIsSyncModalVisible(true);
  };

  const handleAddSiteToPending = () => {
    if (selectedProject && !pendingSites.includes(selectedProject)) {
      setPendingSites([...pendingSites, selectedProject]);
    }
  };

  const handleRemovePendingSite = (siteId: string) => {
    setPendingSites(pendingSites.filter((id) => id !== siteId));
  };

  const getSiteName = (siteId: string): string => {
    const found = sites.find((s) => s.id === siteId);
    return found ? found.name : siteId;
  };

  const handleSyncSiteInDatabase = async () => {
    if (!selectedOrganization) {
      return;
    }

    try {
      setLoading(true);
      await updateProjectOpenSpaceSiteMapping({
        variables: {
          input: {
            customer: project.customer,
            project: project.project,
            organization: selectedOrganization[0],
            sites: pendingSites,
            region: selectedRegion,
          },
        },
      });

      await refetchProjectSiteConfig();
      setIsSyncModalVisible(false);
      notify("Sites synced successfully", "success");
    } catch (error: any) {
      const gqlMessage = error?.graphQLErrors?.[0]?.message;
      notify(
        gqlMessage ||
          "Failed to sync sites, check if you have the correct permissions to update the project config",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      title={
        <div className={styles.layoutHeader}>
          <div>OpenSpace Integration</div>
          <div className={styles.syncButtonContainer}>
            <Button type="primary" onClick={handleOpenSyncModal}>
              Manage Synced Sites ({syncedSites.length})
            </Button>
          </div>
        </div>
      }
      className={styles.layout}
    >
      {generalError && (
        <div className={`${styles.errorMessage} ${styles.bottomError}`}>
          {generalError}
        </div>
      )}
      <div className={styles.formSection}>
        <h3 className={styles.sectionTitle}>Customer Settings</h3>
        <div className={styles.fieldsGrid}>
          <div className={styles.fields}>
            <label className={styles.label}>Openspace Region</label>
            <Select
              showSearch
              optionFilterProp="label"
              className={styles.select}
              placeholder="Select a Region"
              onChange={(value: string) => setSelectedRegion(value)}
              options={REGION_OPTIONS}
              value={selectedRegion}
            />
          </div>
          <div className={styles.fields}>
            <label className={styles.label}>Customer in Openspace</label>
            <Select
              showSearch
              className={styles.select}
              placeholder="Select an Organization"
              onChange={(orgId: string) => {
                const org = organizations.find((o) => o.id === orgId);
                handleOrganizationChange([org.id, org.name]);
              }}
              filterOption={(input, option) =>
                option?.label
                  ?.toLowerCase()
                  ?.indexOf(input.toLowerCase().trim()) >= 0
              }
              options={organizations.map((org: Organization) => ({
                label: org.name,
                value: org.id,
              }))}
              value={selectedOrganization && selectedOrganization[1]}
              disabled={!selectedRegion}
            />
          </div>
        </div>
      </div>

      <div className={styles.formSection}>
        <h3 className={styles.sectionTitle}>Project Settings</h3>
        <div className={styles.fieldsGrid}>
          <div className={styles.fields}>
            <label className={styles.label}>Project in Openspace</label>
            <Select
              showSearch
              className={styles.select}
              placeholder="Select a Project"
              onChange={handleProjectChange}
              disabled={!selectedOrganization}
              filterOption={(input, option) =>
                option?.label
                  ?.toLowerCase()
                  ?.indexOf(input.toLowerCase().trim()) >= 0
              }
              options={sites.map((site: Site) => ({
                label: site.name,
                value: site.id,
              }))}
              value={selectedProject}
            />
          </div>
          <div className={styles.fields}>
            <label className={styles.label}>
              <span>Sheet (Floorplan)</span>
              <Button
                type="link"
                size="small"
                onClick={() => setMappingModalVisible(true)}
                disabled={!selectedProject || siteSheets.length === 0}
                style={{ marginLeft: 8 }}
              >
                Create/Edit Sheet Mappings
              </Button>
            </label>
            <Select
              showSearch
              className={styles.select}
              placeholder="Select a Sheet"
              onChange={handleSiteSheetChange}
              disabled={!selectedProject}
              value={selectedSiteSheet}
              filterOption={(input, option) => {
                const labelText =
                  option?.label?.props?.children || option?.label || "";
                return (
                  labelText.toLowerCase().indexOf(input.toLowerCase().trim()) >=
                  0
                );
              }}
              options={siteSheets.map((sheet: SiteSheet) => ({
                label: (
                  <span
                    style={{
                      color:
                        sheet.sheetStats.videoCaptures.count > 0
                          ? "#008000"
                          : "#ca3433",
                    }}
                  >
                    {sheet.name}
                  </span>
                ),
                value: sheet.id,
              }))}
            />
          </div>
          <div className={styles.fields}>
            <label className={styles.label}>Capture</label>
            <Select
              showSearch
              className={styles.select}
              placeholder="Select a Capture"
              onChange={(value: string) => {
                const captureObj = captures.filter(
                  (capture) => capture.startedAt.split("T")[0] === value,
                );
                const captureObjIds = captureObj.map((capture) => {
                  return [capture.id, value];
                });
                handleCaptureChange(captureObjIds);
              }}
              disabled={!selectedSiteSheet}
              loading={captureLoading}
              value={selectedCapture && selectedCapture[0][1]}
              filterOption={(input, option) =>
                option?.label
                  ?.toLowerCase()
                  ?.indexOf(input.toLowerCase().trim()) >= 0
              }
              options={Object.entries(
                captures.reduce((acc, capture) => {
                  const captureDate = capture.startedAt.split("T")[0];
                  if (!acc[captureDate]) {
                    acc[captureDate] = [];
                  }
                  acc[captureDate].push({ id: capture.id, date: captureDate });
                  return acc;
                }, {} as Record<string, { id: string; date: string }[]>),
              )
                .sort(([dateA], [dateB]) => dateB.localeCompare(dateA))
                .map(([captureDate]) => ({
                  label: captureDate,
                  value: captureDate,
                }))}
            />
          </div>
        </div>
      </div>

      <div className={styles.formSection}>
        <h3 className={styles.sectionTitle}>Floorplan Settings</h3>
        <div className={styles.fieldsGrid}>
          <div className={styles.fields}>
            <label className={styles.label}>Scan Level</label>
            <Select
              showSearch
              className={styles.select}
              placeholder="Select Scan Level"
              onChange={(value: string) => setSelectedScanLevel(value)}
              value={selectedScanLevel}
              filterOption={(input, option) =>
                option?.value
                  ?.toLowerCase()
                  ?.indexOf(input.toLowerCase().trim()) >= 0
              }
            >
              <Select.Option value="EARLY">Early</Select.Option>
              <Select.Option value="MID">Mid</Select.Option>
              <Select.Option value="LATE">Late</Select.Option>
            </Select>
          </div>
          <div className={styles.fields}>
            <label className={styles.label}>Floorplan in OpenSpace Track</label>
            <Select
              className={styles.select}
              placeholder={"Select a floorplan"}
              onChange={floorplansEditor.onChange}
              showSearch
              disabled={!selectedScanLevel}
              value={floorplansEditor.floorplan?.id}
              filterOption={(input, option) =>
                option?.children
                  ?.toLowerCase()
                  ?.indexOf(input.toLowerCase().trim()) >= 0
              }
            >
              {selectFloorplanOptions.map((option) => (
                <Select.Option key={option.id} value={option.id}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>
        {noSequencePoints && floorplansEditor.floorplan?.id && (
          <div className={styles.errorMessage}>
            No sequence points found for the selected floor plan and scan level.
          </div>
        )}
      </div>

      <div className={styles.formSection}>
        <h3 className={styles.sectionTitle}>Adjustment Settings</h3>
        <div className={styles.fieldsRow}>
          <div className={styles.fields}>
            <label className={styles.label}>Move points horizontally</label>
            <InputNumber value={xTranslate} onChange={setXTranslate} />
            <span style={{ marginLeft: "8px" }}>units</span>
          </div>
          <div className={styles.fields}>
            <label className={styles.label}>Move points vertically</label>
            <InputNumber value={yTranslate} onChange={setYTranslate} />
            <span style={{ marginLeft: "8px" }}>units</span>
          </div>
          <div className={styles.fields}>
            <label className={styles.label}>Scale Walkpath</label>
            <InputNumber
              value={adjustableScale}
              onChange={setAdjustableScale}
            />
          </div>
          <div className={styles.fields}>
            <label className={styles.label}>Search Radius Offset</label>
            <InputNumber
              value={avgDistanceOffset}
              onChange={setAvgDistanceOffset}
            />
          </div>
        </div>
      </div>

      <div className={styles.buttonGroup}>
        <Button
          type="primary"
          size="large"
          onClick={handleMapping}
          loading={loading}
          disabled={
            noSequencePoints ||
            !floorplansEditor.floorplan?.id ||
            loading ||
            !selectedCapture ||
            !captureDataLoaded ||
            captureLoading
          }
        >
          Send data for mapping
        </Button>
        <Button
          type="default"
          size="large"
          onClick={showUploadModal}
          disabled={!mappingResponse}
        >
          Start Uploading
        </Button>
        <Button
          type="default"
          size="large"
          onClick={downloadFloorplan}
          loading={downloadLoading}
          disabled={!selectedProject || !selectedSiteSheet || downloadLoading}
        >
          Download Floorplan
        </Button>
      </div>

      {mappingResponse && (
        <div className={styles.imagesContainer}>
          <ClickablePlot
            imageBase64={mappingResponse.plotted_image}
            openspacePoints={
              mappingResponse.filtered_points?.openspace_points?.map(
                (point: number[], index: number) => ({
                  x: point[0],
                  y: point[1],
                  id: mappingResponse.filtered_points.ids[index],
                  captureId: mappingResponse.filtered_points.capture_ids[index],
                  sequenceNumber:
                    mappingResponse.filtered_points.sequencenumbers[index],
                }),
              ) || []
            }
            width={mappingResponse.width}
            height={mappingResponse.height}
            title="Filtered Disperse and Closest OpenSpace Coordinates - Click Yellow circles for panorama"
            onPointClick={handlePointClick}
          />
          <ClickablePlot
            imageBase64={mappingResponse.all_points_plot}
            openspacePoints={panos.map((pano) => ({
              x: pano.location
                ? (pano.location[0] * 0.5 + 0.5) *
                    adjustableScale *
                    mappingResponse.width +
                  xTranslate
                : 0,
              y: pano.location
                ? (pano.location[1] *
                    0.5 *
                    (mappingResponse.width / mappingResponse.height) +
                    0.5) *
                    adjustableScale *
                    mappingResponse.height +
                  yTranslate
                : 0,
              id: pano.id,
              captureId: pano.captureId,
            }))}
            width={mappingResponse.width}
            height={mappingResponse.height}
            title="All Disperse and OpenSpace Coordinates - Click Yellow circles for panorama"
            onPointClick={handlePointClick}
          />
        </div>
      )}

      <Modal
        title={
          uploadStatus.startsWith("Progress:")
            ? "Uploading..."
            : uploadStatus === "Upload completed successfully."
            ? "Uploaded Successfully"
            : uploadStatus === "Re-upload required."
            ? "Re-upload required"
            : "Confirm Upload"
        }
        visible={isUploadModalVisible}
        onOk={
          uploadStatus === "Upload completed successfully."
            ? handleUploadCancel
            : handleUpload
        }
        onCancel={handleUploadCancel}
        okText={
          uploadStatus === "Upload completed successfully."
            ? ""
            : uploadStatus === "Re-upload required."
            ? "Re-upload"
            : "Confirm"
        }
        cancelText={
          uploadStatus === "Upload completed successfully." ? "Close" : "Cancel"
        }
        confirmLoading={uploadLoading}
        afterClose={() => {
          if (uploadStatus === "Upload completed successfully.") {
            setUploadStatus("");
            setUploadPath("");
          }
          setUploadLogs([]);
        }}
      >
        {uploadStatus === "" && (
          <p>Are you sure you want to start uploading?</p>
        )}
        {uploadLoading && <Progress percent={uploadProgress} />}
        <p>{uploadStatus}</p>
        {uploadPath && (
          <p>
            {uploadStatus === "Upload completed successfully."
              ? "Uploaded to:"
              : "Uploading to:"}{" "}
            {uploadPath}
          </p>
        )}
        <div className={styles.modalLogContainer}>
          {uploadLogs.map((log, index) => (
            <div
              key={index}
              className={styles.modalLogEntry}
              style={{
                color:
                  log.toLowerCase().includes("error") ||
                  log.toLowerCase().includes("failed") ||
                  log.toLowerCase().includes("please")
                    ? "red"
                    : "#0f0",
              }}
            >
              {log}
            </div>
          ))}
        </div>
      </Modal>

      <PanoViewerModal
        visible={panoModalVisible}
        onClose={handlePanoModalClose}
        imageUrl={panoImageUrl}
        loading={panoLoading}
        error={panoError}
        pointId={selectedPanoPoint}
      />

      <SheetMappingModal
        visible={mappingModalVisible}
        onClose={() => {
          setMappingModalVisible(false);
          refetchMappedFloorplans();
        }}
        siteSheets={siteSheets}
      />

      <Modal
        title="Manage Synced Sites"
        open={isSyncModalVisible}
        onCancel={() => setIsSyncModalVisible(false)}
        onOk={handleSyncSiteInDatabase}
        okText="Save"
        okButtonProps={{ loading, disabled: !selectedOrganization }}
        width={600}
      >
        <div className={styles.syncModalActions}>
          <Tooltip
            title={
              !selectedOrganization || !selectedProject
                ? "Select an organization and project first"
                : pendingSites.includes(selectedProject)
                ? "This site is already in the list"
                : ""
            }
          >
            <Button
              type="dashed"
              onClick={handleAddSiteToPending}
              disabled={
                !selectedOrganization ||
                !selectedProject ||
                pendingSites.includes(selectedProject)
              }
            >
              + Add currently selected site
            </Button>
          </Tooltip>
        </div>
        <Table
          dataSource={pendingSites.map((siteId) => ({
            key: siteId,
            name: getSiteName(siteId),
            siteId,
          }))}
          pagination={false}
          size="small"
          locale={{ emptyText: "No sites synced" }}
          columns={[
            {
              title: "Site Name",
              dataIndex: "name",
              key: "name",
            },
            {
              title: "Site ID",
              dataIndex: "siteId",
              key: "siteId",
              className: styles.siteIdCell,
            },
            {
              title: "Action",
              key: "action",
              align: "right" as const,
              render: (_value, record) => (
                <Button
                  type="text"
                  danger
                  size="small"
                  onClick={() => handleRemovePendingSite(record.siteId)}
                >
                  Remove
                </Button>
              ),
            },
          ]}
        />
      </Modal>

      {generalError && (
        <div className={`${styles.errorMessage} ${styles.bottomError}`}>
          {generalError}
        </div>
      )}
    </Card>
  );
};
